import { Flex, Form, message, Switch, TableColumnsType } from 'antd';
import * as Component from 'components';
import {
  TransferDataHandlerInterface,
  TransferDataSourceInterface,
} from 'components/transfer/Transfer';
import { seasonService, seriesSeasonItemService } from 'content/service';
import { observer } from 'mobx-react';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { Link, SetURLSearchParams, useNavigate } from 'react-router-dom';
import { SeasonInterface } from 'type/seasonType';
import './Season.css';

interface SeasonProps {
  seriesId: number;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}

const Season: React.FC<SeasonProps> = ({ seriesId, searchParams, setSearchParams }) => {
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [] = Form.useForm();

  const handleOnClickTitle = (id: number): void => {
    navigate(`/season/edit/${id}`);
  };

  const columns = [
    {
      title: 'POSTER',
      dataIndex: 'poster',
      key: 'poster',
      width: '5%',
      render: (_text: string, record: SeasonInterface): JSX.Element => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            style={{ marginLeft: '10px', width: '100%', height: 'auto' }}
            src={record.poster?.file}
            alt=""
          />
        </div>
      ),
    },
    {
      title: 'TITLE',
      dataIndex: 'title',
      key: 'title',
      width: '40%',
      render: (text: string, record: SeasonInterface): JSX.Element => (
        <div className="season-title" onClick={() => handleOnClickTitle(record.id || 0)}>
          <div
            className="title-season"
            style={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {text}
          </div>
        </div>
      ),
    },
    {
      title: 'status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'created (UTC)',
      dataIndex: 'created',
      key: 'created',
      render: (text: string, _: SeasonInterface): JSX.Element => (
        <span>{moment(text).format('YYYY-MM-DD HH:mm:ss')}</span>
      ),
    },
  ];

  const fetchSeason = useCallback(
    async (params: URLSearchParams): Promise<{ data: SeasonInterface[]; count: number }> => {
      const contentList = await seasonService.fetch(
        new URLSearchParams({
          ...Object.fromEntries(params.entries()),
          series_id: seriesId.toString(),
          status: 'published',
        })
      );
      return { data: contentList.results, count: contentList.count };
    },
    [seriesId]
  );

  const transferColumns: TableColumnsType<TransferDataSourceInterface> = [
    {
      dataIndex: 'id',
      title: 'ID',
      render: (id: number) => (
        <span>
          <Link to={`/season/edit/${id}`} target="_blank">
            {id}
          </Link>
        </span>
      ),
    },
    {
      dataIndex: 'name',
      title: 'Name',
    },
    {
      dataIndex: 'season_order',
      title: 'Season Order',
    },
    {
      dataIndex: 'status',
      title: 'Status',
    },
  ];

  const dataHandler: TransferDataHandlerInterface = {
    fetchSourceData: async (
      query: Record<string, string>
    ): Promise<{ data: TransferDataSourceInterface[]; count: number }> => {
      const queryParams = new URLSearchParams(query);
      queryParams.append('order_by', 'season_order');
      const seasons = await seasonService.fetch(queryParams);
      const transferDataSource = seasons.results.map(season => ({
        id: season.id,
        key: season.id,
        name: season.title,
        order_by: 'season_order',
        disabled: false,
        status: season.status,
      }));
      return {
        data: transferDataSource,
        count: seasons.count,
      };
    },
    fetchTargetData: async (): Promise<{ data: TransferDataSourceInterface[]; count: number }> => {
      const queryParams = new URLSearchParams();
      queryParams.append('order_by', 'season_order');
      const seasons = await seriesSeasonItemService.fetchChildItemsByParentId(
        seriesId,
        queryParams
      );

      return {
        data: seasons.map(season => ({
          id: season.id,
          key: season.id,
          name: season.title,
          order_by: 'season_order',
          disabled: false,
        })),
        count: seasons.length,
      };
    },
    createWithParentId: async (itemIds: number[]): Promise<void> => {
      await seriesSeasonItemService.createItemsForParent(seriesId, itemIds);
      message.success('Successfully added to the season');
    },
    deleteWithParentId: async (itemIds: number[]): Promise<void> => {
      await seriesSeasonItemService.deleteItemsForParent(seriesId, itemIds);
      message.success('Successfully removed from the season');
    },
  };

  return (
    <>
      <div style={{ marginBottom: '16px' }}>
        <Switch
          checkedChildren="Edit Mode"
          unCheckedChildren="View Mode"
          onChange={(checked: boolean) => setEditMode(checked)}
          checked={editMode}
        />
      </div>
      {editMode ? (
        <>
          <Flex align="start" gap="middle" vertical>
            <Component.TransferComponent dataHandler={dataHandler} columns={transferColumns} />
          </Flex>
        </>
      ) : (
        <Component.TableComponent<SeasonInterface>
          name="Series Seasons"
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          dataFetch={fetchSeason}
          columns={columns}
        />
      )}
    </>
  );
};

export default observer(Season);
